import { STALENESS } from '../config/timing.js';
export const INITIAL_CONVOY_MEMORY = Object.freeze({
    state: 'in_contact',
    overThresholdSince: null,
    warned: false,
    inContactSince: null,
    leaderMovingSince: null,
});
/** Zustände, die das Führungsfahrzeug erfahren muss. */
const WARNING_STATES = new Set([
    'lost_contact',
    'off_route',
    'standstill',
    'no_signal',
]);
export function isWarningState(state) {
    return WARNING_STATES.has(state);
}
/**
 * Lässt sich für diesen Zustand überhaupt ein Rückstand angeben?
 *
 * Bei „Kein Signal" und „Pausiert" ist die letzte bekannte Position beliebig
 * alt. Ein daraus gerechneter Rückstand ist keine Auskunft, sondern eine
 * Behauptung — am 21.08.2026 stand bei vier Fahrzeugen, die seit 33 Minuten
 * schwiegen, „Kein Signal — Rückstand 00:00". Das eine sagt, wir wissen nichts;
 * das andere behauptet, sie seien genau hier.
 *
 * Eine veraltete Position darf niemals wie eine aktuelle dargestellt werden
 * (Auftrag, Abschnitt 10). Was die App nicht weiß, gibt sie nicht als Zahl aus.
 */
export function delayIsKnown(state) {
    return state !== 'no_signal' && state !== 'paused';
}
function determineState(input, overThresholdSince, leaderMovingSince, vorher) {
    const { settings, now } = input;
    // Der Client hat gemeldet, dass er im Hintergrund ist. Damit endet die
    // Positionserfassung im Browser — das ist nicht umgehbar, aber es ist
    // bekannt, und eine veraltete Position darf nun nicht mehr wie eine aktuelle
    // aussehen.
    if (input.pageVisibility === 'background') {
        return 'paused';
    }
    if (input.lastPositionAt === null ||
        now - input.lastPositionAt > settings.noSignalTimeoutS * 1000) {
        return 'no_signal';
    }
    // Das Führungsfahrzeug ist die Spur; einen Rückstand zu sich selbst hat es nicht.
    if (input.isLeader) {
        return 'in_contact';
    }
    /*
     * Abseits — aber nur, wenn es eine aktuelle Führungsspur gibt.
     *
     * Gemessen wird der Abstand zur Führungsspur. Wächst die Spur nicht mehr,
     * weil die Führung nichts meldet, entfernt sich **jeder** von ihrem letzten
     * bekannten Punkt, der weiterfährt — mit seiner eigenen Geschwindigkeit.
     *
     * Genau so geschehen am 17.08.2026 um 15:05: Die Führung hatte ihre App im
     * Hintergrund, dreizehn Sekunden ohne Meldung — und zwei Fahrzeuge mitten auf
     * der Strecke wurden als Falschabbieger gemeldet. In den Daten wächst ihr
     * Abstand um genau 17 Meter je Sekunde, ihre Fahrgeschwindigkeit.
     *
     * ## Wie lange „aktuell" reicht, hängt am Tempo
     *
     * Eine feste Frist trägt nur bis zu einem bestimmten Tempo. Fünf Sekunden und
     * hundertfünfzig Meter Schwelle passen bis 30 m/s — also 108 km/h. Darüber
     * ist die Schwelle gefahren, **bevor** die Frist abläuft: Die Prüfung schlägt
     * an, statt auszusetzen, und meldet einen Falschabbieger, der keiner ist.
     *
     * Auf der Anfahrt zur Ausfahrt „Bochum-Atteln" am 28.08.2026 lag das Feld
     * 26 % der Zeit über 108 km/h. Im Funkloch der Führung um 08:56 stand es
     * spitz auf Kante:
     *
     *   08:56:38   135 m Abstand   Führung 4 s ohne Meldung
     *   08:56:39   169 m Abstand   Führung 5 s ohne Meldung   ← beides erreicht
     *
     * Es ging um Bruchteile einer Sekunde gut aus, im selben Auto wie die
     * Führung. Die Frist ist deshalb nur so lang, wie das Fahrzeug braucht, um
     * die Schwelle zu fahren — höchstens aber die feste Frist. Damit kann eine
     * stehende Spur die Schwelle nie allein überschreiten, bei keinem Tempo.
     *
     * Meldet das Gerät kein Tempo — bei 0,4 bis 1,9 % der Positionen —, bleibt es
     * bei der festen Frist. Sie ist dann das Beste, was zu haben ist.
     */
    const eigenesTempo = typeof input.speedMps === 'number' && Number.isFinite(input.speedMps) && input.speedMps > 0
        ? input.speedMps
        : null;
    const frist = eigenesTempo === null
        ? STALENESS.trackFreshForOffRouteMs
        : Math.min(STALENESS.trackFreshForOffRouteMs, (settings.offRouteDistanceM / eigenesTempo) * 1000);
    const kopfFrisch = input.trackHeadAt !== null && now - input.trackHeadAt <= frist;
    const abseits = input.offTrackM !== null && input.offTrackM > settings.offRouteDistanceM;
    /*
     * Trägt die Spur an **dieser** Stelle eine Aussage über den Abstand?
     *
     * Zwei Fälle, und nur einer davon braucht eine frische Spur:
     *
     *   * **Mitten auf der Strecke.** Wer dort abbiegt, entfernt sich von einem
     *     Stück Spur, das vollständig aufgezeichnet ist. Ob die Führung gerade
     *     weiterfährt, ändert daran nichts — die Aussage steht.
     *   * **Jenseits des Kopfes.** Dort endet die Aufzeichnung, und der Abstand
     *     wächst schon deshalb, weil vorn nichts mehr steht. Nur hier braucht es
     *     die Frische.
     *
     * Bis zum 03.09.2026 galt die Frischeprüfung für beide. Das hat zweimal
     * geschadet, in entgegengesetzte Richtungen:
     *
     *   * Am 24.08.2026 flackerte der Zustand eines Falschabbiegers zwischen
     *     „Abseits" und „Anschluss", obwohl sein Treffer an der Abzweigung lag —
     *     also mitten auf der Spur. Meldelücken der Führung setzten die Prüfung
     *     aus, und er galt sekundenweise wieder als angeschlossen. A-56 hat
     *     damals das Symptom behandelt und der Entwarnung eine Haltezeit gegeben;
     *     hier ist die Ursache.
     *   * Und umgekehrt: Nach einer Übergabe nach hinten holt der neue Führende
     *     auf, meldet dabei jede Sekunde, und die Spur wächst trotzdem nicht
     *     (A-92). Die halbe Kolonne fuhr über den stehenden Kopf hinaus und
     *     bekam „Abseits" — im Szenario zwei Alarme binnen sieben Sekunden.
     */
    const spurTraegt = !input.amSpurende || kopfFrisch;
    if (spurTraegt && abseits) {
        return 'off_route';
    }
    /*
     * Ist die Spur nicht aktuell, bleibt es bei dem, was zuletzt festgestellt
     * wurde — statt auf etwas anderes zurückzufallen.
     *
     * Am 27.08.2026 wartete eine Kolonne auf einen Falschabbieger. Weil sie dabei
     * **stand**, meldete die Führung nur alle zehn Sekunden
     * (`positionIntervalIdleMs`), und die Spur galt in fünf von zehn Sekunden als
     * nicht aktuell. In der Kolonnenliste wechselte sein Abzeichen im Takt
     * zwischen „Abseits" und „Anschluss".
     *
     * „Wir können es gerade nicht feststellen" ist nicht dasselbe wie „er ist
     * wieder da" — derselbe Satz, mit dem A-56 die Entwarnung begründet.
     *
     * Gehalten wird nur nach oben: Taucht das Fahrzeug messbar wieder **auf** der
     * Spur auf, wird sofort aufgelöst. Eine veraltete Spur kann einen zu grossen
     * Abstand vortäuschen, keinen zu kleinen — deshalb ist der Weg zurück
     * unbedenklich und der Weg hinein nicht.
     */
    if (!spurTraegt && vorher === 'off_route' && abseits) {
        return 'off_route';
    }
    /*
     * „Steht" — aber erst, wenn das Fahrzeug stehen bleibt, **während die
     * Führung fährt**.
     *
     * Beim gemeinsamen Warten läuft die Stillstandsuhr jedes Fahrzeugs ab; das
     * ist unvermeidlich, denn beim Warten stehen alle. Zählte allein sie, wäre
     * der Zustand in dem Augenblick erreicht, in dem die Führung anrollt — und
     * die Warnung träfe den Hintermann, bevor er überhaupt die Kupplung kommen
     * lassen konnte.
     *
     * Auf der Ausfahrt „Atteln_20260825" am 25.08.2026 ist das zweimal
     * geschehen. Beim zweiten Mal stand im Protokoll `delaySeconds=0` — die
     * Schutzregel `standingTogether` hatte also erkannt, dass die beiden
     * beieinander stehen, und den Rückstand nicht umsonst nullgesetzt. Sie
     * korrigiert aber nur den Rückstand, nicht den Zustand. Gewarnt wurde
     * trotzdem, über ein Auto zwei Meter dahinter.
     *
     * Gemessen wird deshalb ab dem späteren der beiden Zeitpunkte: seit wann das
     * Fahrzeug steht, und seit wann die Führung fährt. Wer eine Minute nach der
     * Abfahrt der Führung immer noch nicht angefahren ist, steht wirklich.
     */
    if (leaderMovingSince !== null && input.standingSince !== null) {
        const zaehltAb = Math.max(input.standingSince, leaderMovingSince);
        if (now - zaehltAb >= settings.standstillS * 1000) {
            return 'standstill';
        }
    }
    if (overThresholdSince !== null) {
        return now - overThresholdSince >= settings.gapHoldS * 1000 ? 'lost_contact' : 'lagging';
    }
    return 'in_contact';
}
/**
 * Wertet den Zustand eines Fahrzeugs aus und entscheidet, ob gewarnt wird.
 *
 * Gewarnt wird je Ereignis genau einmal. Zurückgesetzt wird erst, wenn das
 * Fahrzeug wieder Anschluss hat — nicht schon dann, wenn es aus dem Funkloch
 * heraus ist und weiterhin zwei Kilometer zurückliegt. Wiederholte Alarme für
 * denselben Vorgang sind das sicherste Mittel, damit niemand mehr hinsieht.
 */
export function evaluateConvoyState(input, previous = INITIAL_CONVOY_MEMORY) {
    const overThreshold = input.gapSeconds !== null && input.gapSeconds > input.settings.gapThresholdS;
    const overThresholdSince = overThreshold ? (previous.overThresholdSince ?? input.now) : null;
    const leaderMovingSince = input.leaderMoving ? (previous.leaderMovingSince ?? input.now) : null;
    const state = determineState(input, overThresholdSince, leaderMovingSince, previous.state);
    /*
     * Am Sammelpunkt ruht die Warnung über den Rückstand — siehe `leaderWaiting`.
     * Der Zustand bleibt; nur das Wecken unterbleibt.
     *
     * ## Warum das Stehen der Führung **nicht** zusätzlich verlangt wird
     *
     * Naheliegend wäre `!leaderMoving`: Wer durch einen Sammelpunkt hindurchfährt,
     * wartet ja nicht. Gemessen am 15.09.2026 im Szenario `ruhe-am-sammelpunkt`
     * trägt das nicht — die Führung war ab Sekunde 24 im Umkreis und galt erst ab
     * Sekunde 36 als stehend, denn `standstillS` will eine ganze Minute
     * Bestätigung. Die Warnung fiel um Sekunde 33 genau dazwischen, also in den
     * Augenblick, in dem die Kolonne gerade ankommt — und das ist der Fall, für
     * den es die Regel überhaupt gibt.
     *
     * Die Vorbeifahrt schadet dabei nicht, weil eine Unterdrückung die Warnung
     * **verschiebt und nicht löscht**: `warned` bleibt falsch, und beim nächsten
     * Takt außerhalb des Umkreises schlägt sie an. Bei sechzig Stundenkilometern
     * sind das die zwölf Sekunden, die eine Durchfahrt durch zweihundert Meter
     * dauert.
     */
    const erwartetesWarten = input.leaderWaiting && state === 'lost_contact';
    const raiseWarning = isWarningState(state) && !previous.warned && !input.tourPaused && !erwartetesWarten;
    /*
     * Die Entwarnung braucht denselben Beleg wie die Warnung.
     *
     * Vorher genügte ihr ein einziger Takt „Anschluss". Auf der Ausfahrt
     * „Atteln" am 24.08.2026 hat das vier Warnungen für **einen** Vorgang
     * erzeugt: Ein Fahrzeug war durchgehend 617 m neben der Strecke, aber die
     * Meldungen der Führung hatten Lücken von sechs und zehn Sekunden. Über der
     * Frischegrenze von fünf Sekunden fällt die Prüfung auf „Abseits" aus
     * (A-38) — und weil der Rückstand des Fahrzeugs klein war, fiel der Zustand
     * auf „Anschluss" durch. Entwarnung, nächste Meldung der Führung, neue
     * Warnung. Sekundentakt.
     *
     * „Wir können es gerade nicht feststellen" ist nicht dasselbe wie „er ist
     * zurück". Deshalb muss der Anschluss dieselbe Haltezeit überstehen, die auch
     * die Warnung verlangt. Eine Entwarnung ist nichts Dringendes; sie darf
     * warten, bis sie stimmt.
     */
    const inContactSince = state === 'in_contact' ? (previous.inContactSince ?? input.now) : null;
    const clearWarning = previous.warned &&
        inContactSince !== null &&
        input.now - inContactSince >= input.settings.gapHoldS * 1000;
    return {
        state,
        memory: {
            state,
            overThresholdSince,
            inContactSince,
            leaderMovingSince,
            warned: clearWarning ? false : previous.warned || raiseWarning,
        },
        raiseWarning,
        clearWarning,
    };
}
//# sourceMappingURL=state.js.map